import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import styles from './setNewPassword.module.css';
import { resetPassword } from '../services/apiUsers.ts';

export const SetNewPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Get the token from the URL query params
  const urlParams = new URLSearchParams(location.search);
  const token = urlParams.get('token');
  const hasToken = !!token;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate passwords
    if (!password || !confirmPassword) {
      setError('Please enter both password fields');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      // Call the actual reset password API
      await resetPassword(token, password);
      
      // Show success message
      setIsSuccess(true);
      
      // Redirect to sign-in page after a delay
      setTimeout(() => {
        navigate('/sign-in');
      }, 3000);
      
    } catch (err) {
      console.error('Password reset error:', err);
      
      // Handle different error scenarios
      if (err.response?.status === 400) {
        setError('Invalid or expired reset token. Please request a new password reset.');
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // If no token is provided, show an error
  if (!hasToken && !isSuccess) {
    return (
      <div className={styles.container}>
        <div className={styles.formContainer}>
          <div className={styles.formHeader}>
            <h1>Invalid Reset Link</h1>
            <p>The password reset link is invalid or has expired.</p>
          </div>
          <div className={styles.linkContainer}>
            <Link to="/forgot-password" className={styles.backLink}>Request a new reset link</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.formContainer}>
        <div className={styles.formHeader}>
          <h1>Set New Password</h1>
          <p>Create a new password for your account</p>
        </div>
        
        {error && <div className={styles.errorMessage}>{error}</div>}
        
        {isSuccess ? (
          <div className={styles.successMessage}>
            <p>Your password has been reset successfully!</p>
            <p>You will be redirected to the sign-in page shortly...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formGroup}>
              <label htmlFor="password">New Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your new password"
                required
              />
              <small className={styles.passwordHint}>
                Password must be at least 8 characters long
              </small>
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your new password"
                required
              />
            </div>
            
            <button 
              type="submit" 
              className={styles.submitButton}
              disabled={loading}
            >
              {loading ? 'Setting Password...' : 'Set New Password'}
            </button>
            
            <div className={styles.linkContainer}>
              <Link to="/sign-in" className={styles.backLink}>Back to Sign In</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
